import React, { useState } from 'react';
import styles from './index.scss';
import FolderPickerModal from './modal';
import Folder from './folder';
import FolderPath from './path';
import { defineMessages, useIntl } from 'react-intl';
import { SaveLocationTypes } from '../dropin/types';

const messages = defineMessages({
  saveLocation: {
    id: '',
    description: 'Path or location for saving a file',
    defaultMessage: 'Save Location',
  },
  manageDefaults: {
    id: '',
    description: 'Manage default values',
    defaultMessage: 'Manage defaults',
  },
  chooseDefaults: {
    id: '',
    description: 'Select default values',
    defaultMessage: 'Choose default',
  },
  defaultPath: {
    id: '',
    description: 'Default value',
    defaultMessage: 'Default',
  },
  otherPath: {
    id: '',
    description: 'Other location to save a file',
    defaultMessage: 'Other location',
  },
});


type FolderPickerProps = {
  defaultSavePath?: string,
  initialFolderSelection?: string,
  onFolderSelected: (newPath: string) => void,
};

const FolderPicker = (props: FolderPickerProps) => {
  const intl = useIntl();
  const { defaultSavePath, initialFolderSelection, onFolderSelected } = props;
  const [isModalActive, setModalStatus] = useState(false);
  const [saveLocationType, setSaveLocationType] = useState(SaveLocationTypes.Default);
  const onModalClose = () => {
    setModalStatus(false);
  };

  const hasDefaultPath = !!defaultSavePath;
  const currentSavePath = hasDefaultPath ? defaultSavePath : initialFolderSelection;

  const onFolderSelectedFromPicker = (newFolder: string) => {
    onFolderSelected(newFolder);
  };

  const defaultPathMarkup = hasDefaultPath && currentSavePath
    ? <FolderPath path={currentSavePath} />
    : <></>;

  const optionChangeWhenHasDefault = (e: any) => {
    setSaveLocationType(e.target.value);
    if (e.target.value === SaveLocationTypes.Default) {
      if (defaultSavePath) {
        onFolderSelected(defaultSavePath);
      } else {
        onFolderSelected('');
      }
    }
  };

  const pickerMarkup = <Folder
    onFolderSelected={onFolderSelectedFromPicker}
    initialFolderSelection={currentSavePath}
    elementId="dbx-chooser" />;

  const whenHasDefaultMarkup = <>
    <div className={styles.defaultRow}>
      <input type="radio" value={SaveLocationTypes.Default} id="default"
        onChange={optionChangeWhenHasDefault} name="save_option" checked={saveLocationType === SaveLocationTypes.Default} />
      <label htmlFor="default">
        <strong>{intl.formatMessage(messages.defaultPath)}</strong>
        {defaultPathMarkup}
      </label>
    </div>

    <div className={styles.otherRow}>
      <input type="radio" value={SaveLocationTypes.OtherLocation} id="custom"
        onChange={optionChangeWhenHasDefault} name="save_option" checked={saveLocationType === SaveLocationTypes.OtherLocation} />
      <label htmlFor="custom">
        <strong>{intl.formatMessage(messages.otherPath)}</strong>
      </label>
      {saveLocationType === SaveLocationTypes.OtherLocation && pickerMarkup}
    </div>
  </>;

  const finalMarkup = hasDefaultPath
    ? whenHasDefaultMarkup
    : pickerMarkup;

  const manageButtonTitle = hasDefaultPath
    ? intl.formatMessage(messages.manageDefaults)
    : intl.formatMessage(messages.chooseDefaults);

  return (
    <>
      <div className={styles.heading}>
        <a
          className={styles.actionButton}
          onClick={() => { setModalStatus(true); }}
        >
          {manageButtonTitle}
        </a>
        {intl.formatMessage(messages.saveLocation)}
      </div>
      {finalMarkup}
      <FolderPickerModal
        initialFolderSelection={initialFolderSelection}
        onClose={onModalClose}
        onFolderSelected={onFolderSelected}
        isModalActive={isModalActive} />
    </>
  );
};

export default FolderPicker;
